import { Button, Form, IFormRender, Loader, TextareaFormField } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import { useOpenable, useTimeEntry } from 'hooks';
import _ from 'lodash';
import { useRef } from 'react';
import { ClassName } from 'types/ClassName';
import ITimeEntry from 'types/TimeEntry';
import { dateUtils } from 'utils';
import { t } from 'utils/localize';
import { v_require } from 'utils/validations';

export interface IAddTimeEntryDescriptionData {
  description: string;
}

export interface IAddTimeEntryDescriptionDialogProps {
  className?: ClassName;
  timeEntry: ITimeEntry;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (timeEntry: ITimeEntry) => void;
  title?: string;
  description?: string;
  isRequired?: boolean;
}

const AddTimeEntryDescriptionDialog = (props: IAddTimeEntryDescriptionDialogProps) => {
  const { className, timeEntry, isOpen, onClose, onSubmit, title, description, isRequired } = props;

  const { editEntry, editOpenEntry } = useTimeEntry();
  const loaderDetails = useOpenable();
  const formInfo = useRef<IAddTimeEntryDescriptionData>({ description: timeEntry.description });

  function renderFormFields({ ...form }: IFormRender<IAddTimeEntryDescriptionData>) {
    return (
      <div>
        <TextareaFormField
          name="description"
          form={form}
          restrictTo={{ maxLength: 5000 }}
          validations={isRequired ? [{ validation: v_require }] : undefined}
        />
        <div className="pt-6">
          <Button onClick={form.handleSubmit} type="primary">
            {t('Submit')}
          </Button>
        </div>
      </div>
    );
  }

  async function handleSubmit(data: IAddTimeEntryDescriptionData) {
    loaderDetails.open();
    const entry = _.cloneDeep(timeEntry);
    if (!_.isNil(entry.endTime)) {
      const updatedEntryAndLog = await editEntry(
        entry,
        dateUtils.dateTimeFromISOWithoutZone(entry.startTime),
        null,
        data.description === '' ? null : data.description,
        entry.project?.id,
        entry.costCode?.id,
        entry.equipment?.id
      );
      onSubmit(updatedEntryAndLog.entry);
    } else {
      const updatedEntryAndLog = await editOpenEntry(
        entry,
        dateUtils.dateTimeFromISOWithoutZone(entry.startTime),
        data.description === '' ? null : data.description,
        entry.project?.id,
        entry.costCode?.id,
        entry.equipment?.id
      );
      onSubmit(updatedEntryAndLog.entry);
    }
    loaderDetails.close();
  }

  const classes = classNames('add-time-entry-description-dialog', className);

  return (
    <>
      <HeaderDialog
        isOpen={isOpen}
        title={title ?? t('Description')}
        onClose={onClose}
        divider={false}
        className={classes}
      >
        <div>
          <div className="p-8">
            <div className="mb-6">
              <div>{description}</div>
              <Form data={formInfo.current} onSubmit={handleSubmit} render={renderFormFields} className={classes} />
            </div>
          </div>
        </div>
      </HeaderDialog>
      <Loader isOpen={loaderDetails.isOpen} />
    </>
  );
};

export default AddTimeEntryDescriptionDialog;
